import { ROUTES } from "@/core/router/routes";

import { lazy, Suspense } from "react";
import { Outlet } from "react-router-dom";
// auth
import { AuthGuard } from "@/auth/guard";
// layouts

import DashboardLayout from "@/shared/layouts/dashboard-layout/dashboard-layout";
import { SpecificationLayout } from "@/shared/layouts/specification-layout/specification-layout-view";
import SettingLayout from "@/shared/layouts/setting/setting-layout";
import MailPage from "@/features/dashboard/pages/mail/page";
import CompanyFirstCreateGuard from "@/auth/guard/company-first-create-guard";
import SupplierResponsesAllPage from "@/features/dashboard/pages/specification/pages/supplier-responses-all/supplier-responses-all-page";
import DashboardPage from "@/features/dashboard/pages/dashboard/dashboard-page";

const SpecificationListPage = lazy(
  () =>
    import(
      "@/features/dashboard/pages/specification/pages/list/specification-list-page"
    )
);
const SpecificationCreatePage = lazy(
  () =>
    import(
      "@/features/dashboard/pages/specification/pages/create/specification-page"
    )
);

const SettingPage = lazy(
  () => import("@/features/dashboard/pages/setting/pages/setting-page")
);
// here
const ProductCompare = lazy(
  () =>
    import(
      "@/features/dashboard/pages/specification/pages/compare/product-compare"
    )
);
const CMSSoftwarePurchaseReportPage = lazy(
  () =>
    import(
      "@/features/dashboard/pages/specification/pages/cms-software-purchase-report/cms-software-purchase-report"
    )
);
const SupplierResponsesPage = lazy(
  () =>
    import(
      "@/features/dashboard/pages/specification/pages/supplier-responses/supplier-responses-page"
    )
);

const MyProfile = lazy(
  () => import("@/features/dashboard/pages/setting/pages/my-profile")
);

const Integrations = lazy(
  () =>
    import("@/features/dashboard/pages/setting/pages/integrations/integrations")
);

const ProductExplorePage = lazy(
  () =>
    import(
      "@/features/dashboard/pages/specification/pages/explore/product-explore-page"
    )
);

const ProductAnswersPage = lazy(
  () =>
    import(
      "@/features/dashboard/pages/specification/pages/product-answers/product-answers-page"
    )
);

const ProductDiscoveryPage = lazy(
  () =>
    import(
      "@/features/dashboard/pages/specification/pages/product-discovery/product-discovery-page"
    )
);

const CompanyInfo = lazy(
  () => import("@/features/dashboard/pages/setting/pages/company-info")
);
const SecurityAccess = lazy(
  () => import("@/features/dashboard/pages/setting/pages/security-access")
);

const SpecificationTemplatesListPage = lazy(
  () =>
    import(
      "@/features/dashboard/pages/specification/pages/specification-templates/specification-templates-page"
    )
);

const MembersPage = lazy(
  () => import("@/features/dashboard/pages/setting/pages/members")
);

const MemberDetailPage = lazy(
  () => import("@/features/dashboard/pages/setting/pages/members/member")
);

const ProductDiscoverySingleCreatePage = lazy(
  () =>
    import(
      "@/features/dashboard/pages/product-discovery/single/product-discovery-single"
    )
);

const CreateWorkspacePage = lazy(
  () =>
    import("@/features/dashboard/pages/workspace/pages/create/create-workspace")
);

const WorkspaceSwitchLoading = lazy(
  () =>
    import(
      "@/features/dashboard/pages/workspace/pages/workspace-switch-loading.tsx/workspace-switch-loading"
    )
);

const MyWorkspacesPage = lazy(
  () =>
    import(
      "@/features/dashboard/pages/workspace/pages/my-workspaces/my-workspaces-view"
    )
);

const PlansAndBillingsPage = lazy(
  () =>
    import(
      "@/features/dashboard/pages/setting/pages/plans-and-billings/plans-and-billings"
    )
);

const SpecificationMergePage = lazy(
  () =>
    import("@/features/dashboard/pages/specification-merge/specification-merge")
);

// ----------------------------------------------------------------------

export const dashboardRoutes = [
  {
    path: `/${ROUTES.Dashboard.Root}`,
    element: (
      <AuthGuard>
        <CompanyFirstCreateGuard>
          <Suspense>
            <Outlet />
          </Suspense>
        </CompanyFirstCreateGuard>
      </AuthGuard>
    ),
    children: [
      {
        element: (
          <DashboardLayout header={false}>
            <DashboardPage />
          </DashboardLayout>
        ),
        index: true,
      },
      {
        path: ROUTES.Dashboard.MySpecs.Root,
        element: (
          <DashboardLayout>
            <SpecificationListPage />
          </DashboardLayout>
        ),
      },
      {
        path: ROUTES.Dashboard.Specification.List,
        element: (
          <DashboardLayout>
            <SpecificationListPage />
          </DashboardLayout>
        ),
      },
      {
        path: ROUTES.Dashboard.Specification.Create,
        element: <SpecificationCreatePage />,
      },
      {
        path: ROUTES.Dashboard.Specification.ProductAnswers,
        element: (
          <SpecificationLayout disableEdit={true} useProductAnswersSidebar={true}>
            <ProductAnswersPage />
          </SpecificationLayout>
        ),
      },
      {
        path: ROUTES.Dashboard.Setting.Root,
        element: (
          <SettingLayout>
            <SettingPage />
          </SettingLayout>
        ),
      },
      {
        path: ROUTES.Dashboard.Setting.SecurityAccess,
        element: (
          <SettingLayout>
            <SecurityAccess />
          </SettingLayout>
        ),
      },
      {
        path: ROUTES.Dashboard.Inbox.Root,
        element: (
          <DashboardLayout header={false}>
            <MailPage />
          </DashboardLayout>
        ),
      },
      {
        path: ROUTES.Dashboard.Setting.MyProfile,
        element: (
          <SettingLayout>
            <MyProfile />
          </SettingLayout>
        ),
      },
      {
        path: ROUTES.Dashboard.Setting.Integrations,
        element: (
          <SettingLayout>
            <Integrations />
          </SettingLayout>
        ),
      },
      {
        path: ROUTES.Dashboard.Setting.CompanyInfo,
        element: (
          <SettingLayout>
            <CompanyInfo />
          </SettingLayout>
        ),
      },
      {
        path: ROUTES.Dashboard.Setting.Members,
        element: (
          <SettingLayout>
            <MembersPage />
          </SettingLayout>
        ),
      },
      {
        path: ROUTES.Dashboard.Setting.PlansAndBillings,
        element: (
          <SettingLayout>
            <PlansAndBillingsPage />
          </SettingLayout>
        ),
      },
      {
        path: ROUTES.Dashboard.Setting.Workspace.Create,
        element: (
          <>
            <CreateWorkspacePage />
          </>
        ),
      },

      {
        path: ROUTES.Dashboard.Setting.Workspace.SwitchLoading,
        element: (
          <DashboardLayout skeleton={true}>
            <WorkspaceSwitchLoading />
          </DashboardLayout>
        ),
      },

      {
        path: ROUTES.Dashboard.Setting.Workspace.MyWorkspaces,
        element: (
          <DashboardLayout>
            <MyWorkspacesPage />
          </DashboardLayout>
        ),
      },
      {
        path: ROUTES.Dashboard.Setting.MemberDetail,
        element: (
          <SettingLayout>
            <MemberDetailPage />
          </SettingLayout>
        ),
      },
      {
        path: ROUTES.Dashboard.Specification.CompareDetail,
        element: <ProductCompare />,
      },
      {
        path: ROUTES.Dashboard.Specification.Report,
        element: <CMSSoftwarePurchaseReportPage />,
      },
      {
        path: ROUTES.Dashboard.Specification.SupplierResponsesAll,
        element: (
          <DashboardLayout header={false}>
            <SupplierResponsesAllPage />
          </DashboardLayout>
        ),
      },
      {
        path: ROUTES.Dashboard.Specification.SupplierResponses,
        element: (
          <DashboardLayout header={false}>
            <SupplierResponsesPage />
          </DashboardLayout>
        ),
      },
      {
        path: ROUTES.Dashboard.Specification.Skip,
        element: <ProductCompare />,
      },
      {
        path: ROUTES.Dashboard.Specification.Explore,
        element: <ProductExplorePage />,
      },
      {
        path: ROUTES.Dashboard.Specification.ProductDiscovery,
        element: <ProductDiscoveryPage />,
      },
      {
        path: ROUTES.Dashboard.Specification.Templates,
        element: (
          <DashboardLayout>
            <SpecificationTemplatesListPage />
          </DashboardLayout>
        ),
      },
      {
        path: ROUTES.Dashboard.Specification.ProductDiscoverySingleCreate,
        element: (
          <DashboardLayout title="product-discovery">
            <ProductDiscoverySingleCreatePage />
          </DashboardLayout>
        ),
      },
      {
        path: ROUTES.Dashboard.MergeSpecs,
        element: (
          <DashboardLayout header={false}>
            <SpecificationMergePage />
          </DashboardLayout>
        ),
      },
      // {
      //   path: "group",
      //   children: [
      //     { element: <PageFour />, index: true },
      //     { path: "five", element: <PageFive /> },
      //     { path: "six", element: <PageSix /> }
      //   ]
      // }
    ],
  },
];
